// Campagnes côté bailleur : liste, création, page de campagne (onglets) et onglet « Campagnes » de la fiche logement.
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { post, put, del } from "@core/api";
import { date, dateTime, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { extensions } from "@core/modules";
import { useOrg, useRestricted } from "@core/org";
import {
  Button,
  ButtonLink,
  Card,
  Empty,
  ErrorBox,
  Input,
  Loading,
  Modal,
  MoneyInput,
  Notice,
  PageHeader,
  Select,
  Table,
  Tabs,
  Textarea,
  fieldError,
  useToast,
} from "@core/ui";
import { CampaignBadge, REGIMES, parisDateTime, regimeLabel, rentLine } from "./shared";
import { ApplicationsTab, CompareTab } from "./campaignApplications";
import { AnnonceTab, MetricsTab, TemplatesTab, VisitsTab } from "./campaignTabs";

export type Campaign = {
  id: string;
  property_id: string;
  status: string;
  status_label: string;
  title: string;
  description: string;
  lease_regime: string;
  rent_cents: number;
  charges_cents: number;
  charges_mode: string;
  deposit_cents: number;
  available_on: string | null;
  public_slug: string | null;
  short_code: string | null;
  public_url: string | null;
  short_url: string | null;
  published_at: string | null;
  closed_at: string | null;
  created_at: string;
  property_label: string;
  property_furnished: boolean;
  place: string;
  accepting: boolean;
  counts: { by_status: Record<string, number>; total: number; submitted: number; drafts: number };
  next_visit_at?: string | null;
  photos?: { id: string; document_id: string; caption: string; url: string }[];
  events?: { id: string; kind: string; from_status: string | null; to_status: string | null; actor_name: string | null; note: string; details: any; created_at: string }[];
  other_campaigns?: { id: string; title: string; status: string; status_label: string; created_at: string; closed_at: string | null }[];
  deposit_limit_cents?: number | null;
};

const FILTERS = [
  { value: "encours", label: "En cours", statuses: "ouverte,selection" },
  { value: "brouillons", label: "Brouillons", statuses: "brouillon" },
  { value: "terminees", label: "Terminées", statuses: "attribuee,close" },
  { value: "toutes", label: "Toutes", statuses: "" },
] as const;

export function CampaignsPage() {
  const org = useOrg();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["value"]>("encours");
  const statuses = FILTERS.find((f) => f.value === filter)!.statuses;
  const q = useGet<{ items: Campaign[] }>(org.api(`/campaigns${statuses ? `?status=${statuses}` : ""}`));
  return (
    <div className="page">
      <PageHeader
        eyebrow="Location"
        title="Campagnes de location"
        intro="Une campagne par mise en location : annonce, lien de candidature, visites et dossiers. Chaque logement conserve l’historique de ses campagnes."
        actions={org.can("operate") ? <ButtonLink to={org.to("campagnes/nouvelle")}>Nouvelle campagne</ButtonLink> : null}
      />
      <Tabs value={filter} onChange={setFilter} items={FILTERS.map((f) => ({ value: f.value, label: f.label }))} />
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && !q.data.items.length ? (
        <Empty title="Aucune campagne dans cette liste." action={org.can("operate") ? <ButtonLink to={org.to("campagnes/nouvelle")} variant="secondaire">Ouvrir une campagne</ButtonLink> : undefined}>
          Ouvrez une campagne sur un logement, puis partagez le lien de candidature pour recevoir les premiers dossiers.
        </Empty>
      ) : null}
      {q.data?.items.length ? (
        <Table caption="Campagnes de location">
          <thead>
            <tr>
              <th>Campagne</th>
              <th>État</th>
              <th className="right">Dossiers déposés</th>
              <th className="right">À examiner</th>
              <th>Prochaine visite</th>
              <th>Publiée le</th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to={org.to(`campagnes/${c.id}`)}>{c.title}</Link>
                  <span className="block muted small">
                    {c.property_label} · {rentLine(c.rent_cents, c.charges_cents)}
                  </span>
                </td>
                <td>
                  <CampaignBadge status={c.status} label={c.status_label} />
                </td>
                <td className="right num">
                  {c.counts.submitted}
                  {c.counts.drafts ? <span className="block muted small">+ {c.counts.drafts} en cours</span> : null}
                </td>
                <td className="right num">{c.counts.by_status.deposee ?? 0}</td>
                <td>{c.next_visit_at ? parisDateTime(c.next_visit_at, true) : "—"}</td>
                <td>{date(c.published_at)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
    </div>
  );
}

type PropertyOption = {
  id: string;
  label: string;
  kind: string;
  furnished: boolean;
  city: string | null;
  last_campaign: null | { id: string; status: string; status_label: string; lease_regime: string; rent_cents: number; charges_cents: number; charges_mode: string; deposit_cents: number; description: string };
};

export type CampaignForm = {
  title: string;
  description: string;
  lease_regime: string;
  rent_cents: number | null;
  charges_cents: number | null;
  charges_mode: string;
  deposit_cents: number | null;
  available_on: string;
};

/** Plafond légal du dépôt (même règle que le serveur), affiché en aide à la saisie. */
export function depositHelp(regime: string, furnished: boolean, rent: number | null): string {
  if (!rent) return "Montant demandé à la signature du bail.";
  if (regime === "mobilite") return "Aucun dépôt de garantie pour un bail mobilité.";
  if (regime === "vide" || (regime === "colocation" && !furnished)) return `Au plus un mois de loyer hors charges, soit ${money(rent)}.`;
  if (regime === "meuble" || regime === "etudiant" || regime === "colocation") return `Au plus deux mois de loyer hors charges, soit ${money(rent * 2)}.`;
  return "Montant libre pour ce régime ; précisez-le dans le bail.";
}

export function CampaignFields({ form, setForm, error, furnished }: { form: CampaignForm; setForm: (f: CampaignForm) => void; error: unknown; furnished: boolean }) {
  return (
    <>
      <Input label="Titre de l’annonce" placeholder="Ex. T2 lumineux proche du centre" value={form.title} error={fieldError(error, "title")} onChange={(e) => setForm({ ...form, title: e.target.value })} help="Laissé vide, le libellé du logement est utilisé." />
      <div className="form-row">
        <Select label="Régime de location" value={form.lease_regime} options={REGIMES} error={fieldError(error, "lease_regime")} onChange={(e) => setForm({ ...form, lease_regime: e.target.value })} />
        <Input label="Disponible à partir du" type="date" value={form.available_on} onChange={(e) => setForm({ ...form, available_on: e.target.value })} />
      </div>
      <div className="form-row">
        <MoneyInput label="Loyer mensuel hors charges" required cents={form.rent_cents} error={fieldError(error, "rent_cents")} onChange={(c) => setForm({ ...form, rent_cents: c })} />
        <MoneyInput label="Charges mensuelles" cents={form.charges_cents} error={fieldError(error, "charges_cents")} onChange={(c) => setForm({ ...form, charges_cents: c })} />
        <Select
          label="Charges"
          value={form.charges_mode}
          error={fieldError(error, "charges_mode")}
          options={[
            { value: "provision", label: "Provision (régularisée chaque année)" },
            { value: "forfait", label: "Forfait" },
          ]}
          onChange={(e) => setForm({ ...form, charges_mode: e.target.value })}
        />
      </div>
      <MoneyInput label="Dépôt de garantie" cents={form.deposit_cents} error={fieldError(error, "deposit_cents")} help={depositHelp(form.lease_regime, furnished, form.rent_cents)} onChange={(c) => setForm({ ...form, deposit_cents: c })} />
      <Textarea label="Description" rows={6} value={form.description} error={fieldError(error, "description")} onChange={(e) => setForm({ ...form, description: e.target.value })} help="Visible sur la page de candidature. Évitez l’adresse exacte : seule la ville est affichée publiquement." />
    </>
  );
}

export function formPayload(f: CampaignForm) {
  return { ...f, available_on: f.available_on || null, charges_cents: f.charges_cents ?? 0, deposit_cents: f.deposit_cents ?? 0 };
}

export function NewCampaignPage() {
  const org = useOrg();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const props = useGet<{ items: PropertyOption[] }>(org.api("/location/properties"));
  const [propertyId, setPropertyId] = useState(params.get("logement") ?? "");
  const [form, setForm] = useState<CampaignForm>({ title: "", description: "", lease_regime: "vide", rent_cents: null, charges_cents: null, charges_mode: "provision", deposit_cents: null, available_on: "" });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const selected = props.data?.items.find((p) => p.id === propertyId);

  function choose(id: string) {
    setPropertyId(id);
    const p = props.data?.items.find((x) => x.id === id);
    const last = p?.last_campaign;
    setForm({
      ...form,
      lease_regime: last?.lease_regime ?? (p?.furnished ? "meuble" : "vide"),
      rent_cents: last?.rent_cents ?? null,
      charges_cents: last?.charges_cents ?? null,
      charges_mode: last?.charges_mode ?? "provision",
      deposit_cents: last?.deposit_cents ?? null,
      description: last?.description ?? form.description,
    });
    setVersion((v) => v + 1);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const c = await post<Campaign>(org.api("/campaigns"), { property_id: propertyId || null, ...formPayload(form) });
      nav(org.to(`campagnes/${c.id}`));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (!org.can("operate")) return <Notice tone="attention">Votre rôle ne permet pas de créer une campagne.</Notice>;
  return (
    <div className="page">
      <PageHeader eyebrow="Location" title="Nouvelle campagne" intro="La campagne est d’abord un brouillon. Vous l’ouvrez quand l’annonce est prête : le lien de candidature est alors créé." />
      {props.isLoading ? <Loading /> : null}
      <ErrorBox error={props.error} />
      {props.data && !props.data.items.length ? (
        <Notice tone="attention" title="Aucun logement">
          Ajoutez d’abord le logement à louer dans votre patrimoine.
        </Notice>
      ) : null}
      {props.data?.items.length ? (
        <Card>
          <form className="stack" onSubmit={submit}>
            <ErrorBox error={error} />
            <Select
              label="Logement"
              required
              value={propertyId}
              error={fieldError(error, "property_id")}
              options={[{ value: "", label: "Choisissez un logement" }, ...props.data.items.map((p) => ({ value: p.id, label: `${p.label}${p.city ? ` — ${p.city}` : ""}` }))]}
              onChange={(e) => choose(e.target.value)}
            />
            {selected?.last_campaign ? (
              <Notice>
                Conditions reprises de la campagne précédente ({selected.last_campaign.status_label.toLowerCase()}). Elles restent modifiables ; la campagne précédente et ses candidatures ne changent pas.
              </Notice>
            ) : null}
            <div key={version} className="stack">
              <CampaignFields form={form} setForm={setForm} error={error} furnished={!!selected?.furnished} />
            </div>
            <p className="small muted">Les critères financiers que vous fixez pour les dossiers se règlent ensuite depuis la page de la campagne.</p>
            <div className="form-actions">
              <Button type="submit" busy={busy}>
                Créer le brouillon
              </Button>
              <ButtonLink to={org.to("campagnes")} variant="discret">
                Annuler
              </ButtonLink>
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page de campagne

type Transition = { to: string; label: string; variant: "primaire" | "secondaire" | "discret"; perm: "operate" | "decide"; confirm?: string };

function transitions(status: string): Transition[] {
  switch (status) {
    case "brouillon":
      return [
        { to: "ouverte", label: "Ouvrir les candidatures", variant: "primaire", perm: "operate", confirm: "Le lien de candidature devient actif et la page publique est consultable." },
        { to: "close", label: "Abandonner le brouillon", variant: "discret", perm: "operate", confirm: "La campagne sera close sans avoir été publiée." },
      ];
    case "ouverte":
      return [
        { to: "selection", label: "Passer en sélection", variant: "secondaire", perm: "operate", confirm: "Le lien reste consultable mais n’accepte plus de nouvelles candidatures. Les candidats déjà engagés peuvent compléter leur dossier." },
        { to: "attribuee", label: "Marquer comme attribuée", variant: "discret", perm: "decide", confirm: "Indiquez que le logement est attribué. Les autres dossiers restent consultables." },
        { to: "close", label: "Clore la campagne", variant: "discret", perm: "operate", confirm: "Le lien n’acceptera plus de candidature. Une campagne close ne se rouvre pas : créez-en une nouvelle au prochain départ." },
      ];
    case "selection":
      return [
        { to: "ouverte", label: "Rouvrir aux candidatures", variant: "secondaire", perm: "operate" },
        { to: "attribuee", label: "Marquer comme attribuée", variant: "secondaire", perm: "decide", confirm: "Indiquez que le logement est attribué. Les autres dossiers restent consultables." },
        { to: "close", label: "Clore la campagne", variant: "discret", perm: "operate", confirm: "Une campagne close ne se rouvre pas : créez-en une nouvelle au prochain départ." },
      ];
    case "attribuee":
      return [
        { to: "selection", label: "Revenir en sélection", variant: "discret", perm: "decide", confirm: "À utiliser si l’attribution n’aboutit pas (désistement, erreur)." },
        { to: "close", label: "Clore la campagne", variant: "secondaire", perm: "operate", confirm: "Une campagne close ne se rouvre pas : son historique est conservé." },
      ];
    default:
      return [];
  }
}

const TAB_IDS = ["annonce", "candidatures", "visites", "comparaison", "mesures", "reponses"] as const;

export function CampaignPage() {
  const { campaignId = "" } = useParams();
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [params, setParams] = useSearchParams();
  const q = useGet<Campaign>(org.api(`/campaigns/${campaignId}`));
  const [pending, setPending] = useState<Transition | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const restricted = useRestricted();
  const ext = extensions("campaignTabs", { data: q.data, restricted });
  const tab = params.get("onglet") ?? "annonce";
  const setTab = (v: string, extra: Record<string, string> = {}) => setParams({ onglet: v, ...extra }, { replace: true });

  async function apply(t: Transition) {
    setBusy(true);
    setError(null);
    try {
      await post(org.api(`/campaigns/${campaignId}/status`), { status: t.to, note });
      toast("État de la campagne mis à jour.");
      setPending(null);
      setNote("");
      invalidate(org.api("/campaigns"), org.api("/location"));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error ?? new Error("Campagne introuvable.")} />;
  const c = q.data;
  const actions = transitions(c.status).filter((t) => org.can(t.perm));
  const items = [
    { value: "annonce", label: "Annonce et lien" },
    { value: "candidatures", label: `Candidatures (${c.counts.submitted})` },
    { value: "visites", label: "Visites" },
    { value: "comparaison", label: "Comparaison" },
    { value: "mesures", label: "Mesures" },
    { value: "reponses", label: "Réponses types" },
    ...ext.map((e) => ({ value: `ext-${e.id}`, label: e.label })),
  ];
  const current = ext.find((e) => `ext-${e.id}` === tab);
  return (
    <div className="page">
      <PageHeader
        eyebrow={`Campagne · ${c.property_label}`}
        title={c.title}
        intro={
          <span className="row">
            <CampaignBadge status={c.status} label={c.status_label} />
            <span>
              {rentLine(c.rent_cents, c.charges_cents, c.charges_mode)} · {regimeLabel(c.lease_regime)}
              {c.available_on ? ` · disponible le ${date(c.available_on)}` : ""}
            </span>
          </span>
        }
        actions={actions.map((t) => (
          <Button key={t.to} variant={t.variant} small onClick={() => (t.confirm ? setPending(t) : apply(t))} busy={busy && pending === null}>
            {t.label}
          </Button>
        ))}
      />
      {!pending ? <ErrorBox error={error} /> : null}
      <Tabs value={TAB_IDS.includes(tab as any) || current ? tab : "annonce"} onChange={(v) => setTab(v)} items={items} />
      {tab === "annonce" || (!TAB_IDS.includes(tab as any) && !current) ? <AnnonceTab campaign={c} /> : null}
      {tab === "candidatures" ? <ApplicationsTab campaign={c} onCompare={(ids) => setTab("comparaison", { ids: ids.join(",") })} /> : null}
      {tab === "visites" ? <VisitsTab campaign={c} /> : null}
      {tab === "comparaison" ? <CompareTab campaign={c} ids={(params.get("ids") ?? "").split(",").filter(Boolean)} onSelect={(ids) => setTab("comparaison", ids.length ? { ids: ids.join(",") } : {})} /> : null}
      {tab === "mesures" ? <MetricsTab campaign={c} /> : null}
      {tab === "reponses" ? <TemplatesTab /> : null}
      {current ? <current.Component orgId={org.orgId} campaignId={c.id} /> : null}
      <Modal
        open={!!pending}
        onClose={() => setPending(null)}
        title={pending?.label ?? ""}
        footer={
          <>
            <Button variant="discret" onClick={() => setPending(null)}>
              Annuler
            </Button>
            <Button busy={busy} onClick={() => pending && apply(pending)}>
              Confirmer
            </Button>
          </>
        }
      >
        <div className="stack">
          <p>{pending?.confirm}</p>
          <ErrorBox error={error} />
          <Textarea label="Note pour l’historique (facultatif)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Onglet de la fiche logement (page du module patrimoine)

export function PropertyCampaignsTab({ orgId, propertyId }: { orgId: string; propertyId: string }) {
  const org = useOrg();
  const q = useGet<{ items: Campaign[] }>(`/api/v1/orgs/${orgId}/campaigns?property_id=${propertyId}`);
  return (
    <Card
      title="Campagnes de location"
      actions={org.can("operate") ? <ButtonLink small variant="secondaire" to={`/espace/${orgId}/campagnes/nouvelle?logement=${propertyId}`}>Nouvelle campagne</ButtonLink> : null}
    >
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && !q.data.items.length ? <p className="muted">Aucune campagne pour ce logement. Ouvrez-en une pour recevoir des dossiers.</p> : null}
      {q.data?.items.length ? (
        <ul className="list-plain location-timeline">
          {q.data.items.map((c) => (
            <li key={c.id}>
              <Link to={`/espace/${orgId}/campagnes/${c.id}`}>{c.title}</Link> <CampaignBadge status={c.status} label={c.status_label} />
              <span className="block muted small">
                Créée le {date(c.created_at)}
                {c.closed_at ? ` · close le ${date(c.closed_at)}` : ""} · {c.counts.submitted} dossier{c.counts.submitted > 1 ? "s" : ""} déposé{c.counts.submitted > 1 ? "s" : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

/** Historique d'une campagne (création, états, conditions, photos). */
export function CampaignHistory({ events }: { events: NonNullable<Campaign["events"]> }) {
  const label = (e: NonNullable<Campaign["events"]>[number]) => {
    switch (e.kind) {
      case "creee":
        return "Campagne créée";
      case "statut":
        return `État : ${e.to_status ? (CAMPAIGN_LABEL[e.to_status] ?? e.to_status) : ""}`;
      case "conditions_modifiees":
        return `Conditions modifiées (${Object.keys(e.details ?? {}).join(", ")})`;
      case "photo_ajoutee":
        return "Photo ajoutée";
      case "photo_retiree":
        return "Photo retirée";
      default:
        return e.kind;
    }
  };
  return (
    <ol className="list-plain location-timeline">
      {events.map((e) => (
        <li key={e.id}>
          <strong>{label(e)}</strong>
          <span className="block muted small">
            {dateTime(e.created_at)}
            {e.actor_name ? ` · ${e.actor_name}` : ""}
            {e.note ? ` · « ${e.note} »` : ""}
          </span>
        </li>
      ))}
    </ol>
  );
}

const CAMPAIGN_LABEL: Record<string, string> = {
  brouillon: "brouillon",
  ouverte: "ouverte aux candidatures",
  selection: "sélection en cours",
  attribuee: "attribuée",
  close: "close",
};

export async function saveCampaign(orgApi: (p: string) => string, id: string, form: CampaignForm) {
  return put<Campaign>(orgApi(`/campaigns/${id}`), formPayload(form));
}

export async function deleteCampaign(orgApi: (p: string) => string, id: string) {
  return del(orgApi(`/campaigns/${id}`));
}
